<script setup>
import BaseInputMoney from '../../base/BaseInputMoney.vue'
import { computed, onMounted, watch, ref } from 'vue'
import { getCampaignData, ClearingSystem } from '../../../utilities/investment-process/useCampaign'
import BaseInputNumber from '../../base/BaseInputNumber.vue'
import PreEmptiveRightsInfoBanner from './../secondaryMarket/PreEmptiveRightsInfoBanner.vue'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { storeToRefs } from 'pinia'
import BaseButtonPrimary from '../../base/BaseButtonPrimary.vue'
import { formatCurrency, roundToPrecision } from '../../../utilities/helpers'
import { navigateToNextStep } from '../../../utilities/account/useInvestmentProcessNavigation'
import { retrievePreEmptiveRights as apiRetrievePreEmptiveRights } from '../../../utilities/api'
import { isLegalProfile } from '../../../utilities/investment-process/useUserProfileType'

const { investmentAmount, incentiveUid, voucherifyVoucherUid, voucherAmount, appliedCoupons, walletAmount, pieces } =
    storeToRefs(useInvestmentProcessDataState())

// -----------------------------------------------------------------------
// PRE-EMPTIVE RIGHTS & SUBSCRIPTION RATIO LOGIC
// -----------------------------------------------------------------------
const availablePreEmptiveRights = ref(0)
const isRightsLoading = ref(false)
// pre-emptive rights are tracked with 5 decimal places (e.g. subscription ratio 4.1)
const PRE_EMPTIVE_RIGHTS_DECIMALS = 5

const isPreEmptiveRightsRequired = computed(() => {
    return !!getCampaignData()?.asset_data?.sm_pre_emptive_rights_required
})

const subscriptionRatio = computed(() => {
    const ratio = Number(getCampaignData()?.asset_data?.sm_subscription_ratio)
    return isNaN(ratio) || ratio <= 0 ? 1 : ratio
})

const requiredPreEmptiveRights = computed(() => {
    const calculatedPieces = Number(pieces.value) || 0
    return roundToPrecision(calculatedPieces * subscriptionRatio.value, PRE_EMPTIVE_RIGHTS_DECIMALS)
})

const isInvestmentInvalid = computed(() => {
    if (!investmentAmount.value || investmentAmount.value < getCampaignData().asset_data.investment_min) {
        return true
    }

    if (isPreEmptiveRightsRequired.value) {
        if (availablePreEmptiveRights.value <= 0 || requiredPreEmptiveRights.value > availablePreEmptiveRights.value) {
            return true
        }
    }

    return false
})

function validateAndAdjustPreEmptiveRights() {
    if (!isPreEmptiveRightsRequired.value) {
        return true
    }

    if (availablePreEmptiveRights.value <= 0 || requiredPreEmptiveRights.value > availablePreEmptiveRights.value) {
        const maxAllowedShares = Math.floor(
            roundToPrecision(availablePreEmptiveRights.value / subscriptionRatio.value, PRE_EMPTIVE_RIGHTS_DECIMALS)
        )
        const stepLevel = getCampaignData()?.asset_data?.step_level || 1

        pieces.value = maxAllowedShares
        investmentAmount.value = maxAllowedShares * stepLevel
        return false
    }

    return true
}

async function retrievePreEmptiveRights() {
    if (!isPreEmptiveRightsRequired.value) return

    isRightsLoading.value = true
    try {
        const res = await apiRetrievePreEmptiveRights(
            isLegalProfile() ? 'legal' : 'natural',
            getCampaignData().asset_data.asset_uid
        )
        availablePreEmptiveRights.value = res?.data?.available_pre_emptive_rights ?? res?.data?.rights ?? 0
        validateAndAdjustPreEmptiveRights()
    } catch (e) {
        console.error('Failed to retrieve pre-emptive rights:', e)
        availablePreEmptiveRights.value = 0
    } finally {
        isRightsLoading.value = false
    }
}

function handleNextStep() {
    if (isInvestmentInvalid.value) {
        return
    }

    if (validateAndAdjustPreEmptiveRights()) {
        navigateToNextStep()
    }
}

// -----------------------------------------------------------------------
// EXISTING CAMPAIGN & INVESTMENT LOGIC
// -----------------------------------------------------------------------
const investmentMax = computed(() => {
    let investmentMax = 0
    const remainingFundingCapacity = Math.max(
        0,
        getCampaignData().asset_data.funding_max - getCampaignData().asset_data.funding_amount
    )
    if (getCampaignData().asset_data.investment_max > remainingFundingCapacity) {
        investmentMax = remainingFundingCapacity
    } else {
        investmentMax = getCampaignData().asset_data.investment_max
    }
    return investmentMax
})

const disableInvestmentAmount = computed(
    () =>
        getCampaignData().asset_data.clearing_system.name === ClearingSystem.EMISSION &&
        window.emissionHideInvestmentAmountRow
)

const localPieces = computed({
    get() {
        return (investmentAmount.value / getCampaignData().asset_data.step_level).toFixed(0)
    },
    set(newValue) {
        if (newValue < 1) {
            newValue = 1
        }
        pieces.value = newValue
        investmentAmount.value = pieces.value * getCampaignData().asset_data.step_level
    },
})

const currencySymbol = ref('')

function adjustInvestmentAmountToCampaignRestrictions() {
    // Make sure that investment amount is a multiple of step level
    investmentAmount.value =
        Math.floor(investmentAmount.value / getCampaignData().asset_data.step_level) *
        getCampaignData().asset_data.step_level
    // Make sure that investment amount is not lower than the investment minimum
    if (investmentAmount.value < getCampaignData().asset_data.investment_min) {
        investmentAmount.value = getCampaignData().asset_data.investment_min
    }
    // Make sure that investment amount is not higher than the investment maximum
    if (investmentAmount.value > investmentMax.value) {
        investmentAmount.value = investmentMax.value
    }
    // set appropriate incentive
    if (getCampaignData().assets_incentives.length > 0) {
        const sortedIncentives = [...getCampaignData().assets_incentives].sort(
            (a, b) => b.incentives_amount - a.incentives_amount
        )
        const incentive = sortedIncentives.find((incentive) => incentive.incentives_amount <= investmentAmount.value)
        if (incentive) {
            incentiveUid.value = incentive.asset_inc_uid
        }
    }

    validateAndAdjustPreEmptiveRights()
}

function calcPieces() {
    if (getCampaignData().asset_data.clearing_system.name === ClearingSystem.EMISSION) {
        pieces.value = investmentAmount.value / getCampaignData().asset_data.step_level
    }
}

function getAgioDisagioTextLabel() {
    if (getCampaignData().asset_data.agio_disagio === 1) {
        return 'investment_process.emission_agio_text'
    } else if (getCampaignData().asset_data.agio_disagio === 2) {
        return 'investment_process.emission_disagio_text'
    }
}

onMounted(() => {
    retrievePreEmptiveRights()

    // set initial value for investment amount if not set (eg. via pending investment cookie)
    if (!investmentAmount.value) {
        investmentAmount.value = getCampaignData().asset_data.investment_min
    }
    currencySymbol.value = getCampaignData().asset_data.currency.replace('&euro;', '€')
})

watch(investmentAmount, (val) => {
    walletAmount.value = null
    voucherifyVoucherUid.value = null
    voucherAmount.value = null
    appliedCoupons.value = []
    calcPieces()
    validateAndAdjustPreEmptiveRights()
})
</script>

<template>
    <div>
        <!-- PRE-EMPTIVE RIGHTS BANNER -->
        <PreEmptiveRightsInfoBanner
            :is-required="isPreEmptiveRightsRequired"
            :is-loading="isRightsLoading"
            :available-rights="availablePreEmptiveRights"
            :subscription-ratio="subscriptionRatio"
            :shares="pieces" />

        <div v-if="!disableInvestmentAmount">
            <p class="pb-3 text-base font-bold text-gray-700">
                {{ $t('investment_process.enter_investment_amount') }}
            </p>
            <div class="grid grid-cols-1 items-center md:grid-cols-4 lg:space-x-4">
                <BaseInputMoney
                    v-model="investmentAmount"
                    name="investment_amount"
                    class="mb-2 text-center md:text-left"
                    :currency="getCampaignData().asset_data.currency_string"
                    autofocus
                    label=""
                    :classes="investmentAmount === getCampaignData().asset_data.investment_min ? 'text-gray-400' : ''"
                    size="xl"
                    @blur="adjustInvestmentAmountToCampaignRestrictions"></BaseInputMoney>
                <p
                    v-if="getCampaignData().asset_data.clearing_system.name === ClearingSystem.EMISSION"
                    class="mb-2 text-left text-base text-gray-700">
                    {{
                        $t('investment_process.emission_amount_to_pieces', {
                            pieces: localPieces,
                        })
                    }}

                    <span v-if="getCampaignData().asset_data.agio_disagio !== 0" class="font-bold">{{
                        $t(getAgioDisagioTextLabel(), {
                            amount: (() => {
                                const value = pieces * getCampaignData().asset_data.agio_disagio_rate
                                return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)
                            })(),
                        })
                    }}</span>
                </p>
                <div class="mb-2 space-y-0.5 text-gray-700 md:text-xs">
                    <p class="text-left">
                        <strong>{{ $t('investment_process.min_amount') }}</strong
                        >:
                        {{
                            formatCurrency(
                                getCampaignData().asset_data.investment_min,
                                getCampaignData().asset_data.currency_string,
                                0
                            )
                        }}
                    </p>
                    <p class="text-left">
                        <strong>{{ $t('investment_process.step_level') }}</strong
                        >:
                        {{
                            formatCurrency(
                                getCampaignData().asset_data.step_level,
                                getCampaignData().asset_data.currency_string,
                                0
                            )
                        }}
                    </p>
                </div>
                <div class="flex items-center justify-center">
                    <BaseButtonPrimary
                        class="w-full md:w-auto"
                        :disabled="isInvestmentInvalid"
                        @click="handleNextStep()"
                        >{{ $t('investment_process.invest') }}
                    </BaseButtonPrimary>
                </div>
            </div>
        </div>
        <div class="my-6 h-0.5 w-full bg-gray-300/60 md:hidden"></div>
        <div v-if="getCampaignData().asset_data.clearing_system.name === ClearingSystem.EMISSION">
            <p class="pb-3 pt-4 text-base font-bold text-gray-700">
                {{
                    disableInvestmentAmount
                        ? $t('investment_process.enter_investment_pieces_only')
                        : $t('investment_process.enter_investment_pieces')
                }}
            </p>
            <div class="grid grid-cols-1 items-center md:grid-cols-4">
                <div class="text-xl">
                    <BaseInputNumber
                        v-model="localPieces"
                        class="mb-2 text-center text-gray-600"
                        name="pieces"
                        size="xl"
                        currency="Anteile"
                        label=""
                        @click="$event.target.select()"></BaseInputNumber>
                </div>
                <p class="mb-2 text-left text-base text-gray-700">
                    {{
                        $t('investment_process.emission_pieces_to_amount', {
                            investmentAmount: investmentAmount,
                            currency_symbol: currencySymbol,
                        })
                    }}

                    <span v-if="getCampaignData().asset_data.agio_disagio !== 0" class="font-bold">{{
                        $t(getAgioDisagioTextLabel(), {
                            amount: (() => {
                                const value = pieces * getCampaignData().asset_data.agio_disagio_rate
                                return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)
                            })(),
                        })
                    }}</span>
                </p>
                <div class="mb-2 space-y-0.5 text-gray-700 md:text-xs">
                    <p class="text-left">
                        <strong>{{ $t('investment_process.min_amount') }}</strong
                        >:
                        {{
                            formatCurrency(
                                getCampaignData().asset_data.investment_min,
                                getCampaignData().asset_data.currency_string,
                                0
                            )
                        }}
                    </p>
                    <p class="text-left">
                        <strong>{{ $t('investment_process.step_level') }}</strong
                        >:
                        {{
                            formatCurrency(
                                getCampaignData().asset_data.step_level,
                                getCampaignData().asset_data.currency_string,
                                0
                            )
                        }}
                    </p>
                </div>
                <div class="flex items-center justify-center">
                    <BaseButtonPrimary
                        class="w-full md:w-auto"
                        :disabled="isInvestmentInvalid"
                        @click="handleNextStep()"
                        >{{ $t('investment_process.invest') }}
                    </BaseButtonPrimary>
                </div>
            </div>
        </div>

        <div v-if="getCampaignData().asset_data.show_amount_slider" class="py-4">
            <input
                v-model="investmentAmount"
                :step="getCampaignData().asset_data.step_level"
                type="range"
                class="range accent-primary"
                :min="getCampaignData().asset_data.investment_min"
                :max="investmentMax" />
            <div></div>
            <div class="flex justify-between text-sm font-semibold text-gray-700">
                <p>{{ formatCurrency(getCampaignData().asset_data.investment_min) }}</p>
                <p>{{ formatCurrency(investmentMax) }}</p>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
